import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  Actif,
  CreateOrUpdatePatrimoineGQL,
  DeletePatrimoineGQL,
  GetPatrimoineCaregorieReferenceGQL,
  Investisseur,
  InvestisseurPatrimoine,
  InvestisseurPatrimoinesByCodeGQL,
  ManageInvestisseurRetraiteGQL,
  Passif,
  Patrimoine,
  PatrimoineCategorieReference,
  PatrimoineCategoryEnum,
  PatrimoineInput,
  PersonnePhysique,
  SyntheseEpargne,
  TypeDetenteurEnum,
  TypeDetentionEnum,
  TypePassif,
  UpdatePatrimoineContratGQL,
} from '@lib/models/generated/graphql';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { QueryManagerService } from 'finzzle-angular-utils';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { MenuNode } from 'omnium-ui/shared';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { BudgetService } from '../budget-step/budget.service';
import { RecueilInformationsModel } from '../recueil-informations/models/recueil-informations.model';
import { DetenteurPersonne } from './ajouter-patrimoine/patrimoine-detenteur-table/patrimoine-detenteur-table.component';
import { DetenteurCategorie, DetenteurOption } from './models/detenteur-option.type';
import { actifTypes, passifTypes, prevoyanceTypes } from './models/patrimoine-form.model';
import { InvestisseurPatrimoineView, PatrimoineView } from './models/patrimoine-view.model';
import { PatrimoineCalculatorService } from './patrimoine-calculator.service';
export enum AccessMode {
  ADD = 0,
  UPDATE = 1,
  VIEW = 3,
}
@Injectable({ providedIn: 'root' })
export class PatrimoineService {
  private readonly createOrUpdatePatrimoineGQL = inject(CreateOrUpdatePatrimoineGQL);
  private readonly updatePatrimoineContratGQL = inject(UpdatePatrimoineContratGQL);
  private readonly deletePatrimoineGQL = inject(DeletePatrimoineGQL);
  private readonly getPatrimoineCaregorieReferenceGQL = inject(GetPatrimoineCaregorieReferenceGQL);
  private readonly investisseurPatrimoineByCodeGQL = inject(InvestisseurPatrimoinesByCodeGQL);
  private readonly manageInvestisseurRetraiteGQL = inject(ManageInvestisseurRetraiteGQL);
  private readonly queryManagerService = inject(QueryManagerService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly investisseurService = inject(InvestisseurService);
  private readonly budgetService = inject(BudgetService);
  private readonly patrimoineCalculatorService = inject(PatrimoineCalculatorService);
  public readonly recueilInformations = signal<RecueilInformationsModel | undefined>(undefined);
  private readonly investisseurCode = computed(() => this.recueilInformations()?.identite.investisseur.codeInvestisseur);
  private readonly coInvestisseurCode = computed(() => {
    return this.recueilInformations()?.identite.conjoint?.codeInvestisseur;
  });
  public readonly investisseurIds = computed(() => {
    const investisseurId = this.investisseurService.investisseurSig()?.id;
    const coInvestisseurId =
      this.investisseurService.investisseurSig()?.personnePhysique[0].relationCouplePersonnePhysique?.autrePersonnePhysique?.investisseur[0]
        ?.id;
    return [investisseurId, coInvestisseurId].filter((id): id is number => id !== undefined);
  });
  public readonly investisseurNom = computed(() => {
    const personne = this.investisseurService.investisseurSig()?.personnePhysique[0];
    return personne?.nom + ' ' + personne?.prenom;
  });
  public readonly coInvestisseurNom = computed(() => {
    const coPersonne =
      this.investisseurService.investisseurSig()?.personnePhysique[0].relationCouplePersonnePhysique?.autrePersonnePhysique;
    return coPersonne?.nom + ' ' + coPersonne?.prenom;
  });
  public readonly childrenInvestisseurIds = computed(() => {
    const relationFamille = this.investisseurService.investisseurSig()?.personnePhysique[0].relationFamillePersonnePhysique;
    const childrenIds =
      relationFamille
        ?.filter(rel => rel.typeRelationFamilleCode === 'PARENT')
        .map(rel => rel.autrePersonnePhysique?.investisseur[0]?.id)
        .filter((id): id is number => id !== undefined) ?? [];
    return childrenIds;
  });
  readonly formLoaded = signal<boolean>(false);
  private readonly _investisseurPatrimoines = signal<InvestisseurPatrimoine[]>([]);
  readonly investisseurPatrimoines = this._investisseurPatrimoines.asReadonly();
  readonly patrimoines = computed<Patrimoine[]>(() => this.investisseurPatrimoines().map(ip => ip.patrimoine as Patrimoine));
  readonly patrimoineViews = computed<PatrimoineView[]>(() => {
    const list = this.patrimoines();
    const result: PatrimoineView[] = [];

    for (const item of list) {
      const fiscalMap = this.patrimoineCalculatorService.computeFiscalValues(this.getValeurBien(item) ?? 0, item.investisseurPatrimoines);
      const fiscalMapSecurisee = this.patrimoineCalculatorService.computeFiscalValues(
        item.patrimoineActif?.epargneSecurise ?? 0,
        item.investisseurPatrimoines
      );

      const mappedItem: PatrimoineView = {
        ...item,
        investisseurPatrimoinesDisplayed: this.getInvestisseurPatrimoinesDisplayed(item, fiscalMap, fiscalMapSecurisee),
        investisseurPatrimoines: item.investisseurPatrimoines.map(i => {
          const tauxDetention = i.detentionPP ?? 0;
          const baseMensualite = this.getCurrentMensualite(item.patrimoinePassif);
          const mensualiteDetention = (baseMensualite * tauxDetention) / 100;

          const rawDateFin = item.patrimoinePassif?.dateFin;

          return {
            ...i,
            valeurDetention: fiscalMap?.get(i.id) ?? 0,
            epargneSecurisee: fiscalMapSecurisee?.get(i.id) ?? 0,
            dateFinPassif: rawDateFin ? new Date(rawDateFin) : null,
            mensualiteDetention: mensualiteDetention,
          };
        }),
        type: this.getType(item),
      };

      result.push(mappedItem);
    }

    return result;
  });
  readonly patrimoineSelected = signal<any | undefined>(undefined);
  readonly formInitied = signal<boolean>(false);
  readonly syntheseEpargne = signal<SyntheseEpargne | null>(null);
  readonly accordionToTarget = signal<string>('');
  readonly patrimoineTabOpen = signal<string[]>([]);
  readonly formType = signal<MenuNode>({} as MenuNode);
  readonly formCategory = signal<string>('');
  readonly creditToBeAssociate = signal<InvestisseurPatrimoine[]>([]);
  private readonly reloadPatrimoinesSignal = signal(0);
  private activeFetchId = 0;
  public readonly patrimoineCategorieReference = signal<PatrimoineCategorieReference[]>([]);
  public readonly loading = signal<boolean>(false);

  constructor() {
    this.getPatrimoineCategorie();
  }

  private async getPatrimoineCategorie() {
    const result = await this.queryManagerService.handleQueryErrorsAsync(this.getPatrimoineCaregorieReferenceGQL.fetch());
    this.patrimoineCategorieReference.set(result.data!.patrimoineCategoriePerefences as PatrimoineCategorieReference[]);
  }
  private readonly loadPatrimoinesEffect = effect(() => {
    const code = this.investisseurCode();
    void this.reloadPatrimoinesSignal();
    if (!code) {
      this.loading.set(false);
      this._investisseurPatrimoines.set([]);
      this.syntheseEpargne.set(null);
      return;
    }
    const fetchId = ++this.activeFetchId;
    this.fetchPatrimoines(code, fetchId);
  });
  public requestPatrimoinesReload(): void {
    this.reloadPatrimoinesSignal.update(token => token + 1);
  }
  private async fetchPatrimoines(code: string, fetchId: number): Promise<void> {
    this.loading.set(true);
    try {
      // Ici true pour catch l'erreur sur la récupération de la personnePhysique
      // Et gestion des autres erreurs renvoyé à la librairie pour afficher la snackbar
      const main = await this.queryManagerService.handleQueryErrorsAsync(
        this.investisseurPatrimoineByCodeGQL.fetch({
          fetchPolicy: 'network-only',
          variables: { code },
        }),
        true
      );
      this.investisseurService.manageErrorInvestisseurOutOfWallet(
        this.queryManagerService.getGraphQLErrors(main),
        'investisseurPatrimoines'
      );
      if (this.activeFetchId !== fetchId || this.investisseurCode() !== code) return;
      const coCode = this.coInvestisseurCode();
      // Ici true pour catch l'erreur sur la récupération de la personnePhysique
      // Et gestion des autres erreurs renvoyé à la librairie pour afficher la snackbar
      const co = coCode
        ? await this.queryManagerService.handleQueryErrorsAsync(
            this.investisseurPatrimoineByCodeGQL.fetch({
              fetchPolicy: 'network-only',
              variables: { code: coCode },
            }),
            true
          )
        : null;

      if (this.activeFetchId !== fetchId || this.investisseurCode() !== code) return;
      const mainIPs = main?.data?.investisseurByCode?.investisseurPatrimoines ?? [];
      const coIPs = co?.data?.investisseurByCode?.investisseurPatrimoines ?? [];
      const merged = [...mainIPs, ...coIPs.filter(coIp => !mainIPs.some(invIp => invIp.patrimoine.id === coIp.patrimoine.id))];
      const next = [...merged].sort((a, b) => (b.patrimoine.id ?? 0) - (a.patrimoine.id ?? 0));
      this._investisseurPatrimoines.set(next as InvestisseurPatrimoine[]);
      this.syntheseEpargne.set(main?.data?.investisseurByCode?.syntheseEpargne ?? null);
    } finally {
      if (this.activeFetchId === fetchId && this.investisseurCode() === code) {
        this.loading.set(false);
      }
    }
  }
  // START REFACTO
  public getDetenteurOptions(categorie: DetenteurCategorie = 'default') {
    const investisseur = this.investisseurService.investisseurSig();
    if (!investisseur) return [];
    const personne = investisseur.personnePhysique[0];
    if (!personne) return [];
    const detenteur = this.getDetenteurOption(investisseur.id, personne);
    const result: DetenteurOption[] = [detenteur];
    const coInvestisseurPersonne = personne.relationCouplePersonnePhysique?.autrePersonnePhysique;
    if (coInvestisseurPersonne) {
      const coInvestisseur = coInvestisseurPersonne.investisseur[0];
      const coDetenteur = this.getDetenteurOption(coInvestisseur.id, coInvestisseurPersonne);
      result.push(coDetenteur);
      const isMaried = personne.relationCouplePersonnePhysique?.typeRelationCoupleCode === 'MARIE';
      const regimeMatrimonial = personne.etatCivil?.typeRegimeSituationEtatCivilCode;
      const isSeparationBiens =
        regimeMatrimonial === 'PART_ACQ' || regimeMatrimonial === 'SEP_BIENS' || regimeMatrimonial === 'SEP_BIENS_ACQ';
      const detenteurIds = `${investisseur.id},${coInvestisseur.id}`;
      if (categorie === 'actif' && isMaried && !isSeparationBiens) {
        result.push({ label: 'La communauté', value: { type: TypeDetenteurEnum.LaCommunaute, ids: detenteurIds } });
      }
      if (categorie === 'passif') {
        if (isMaried && !isSeparationBiens) {
          result.push({
            label: 'La communauté',
            value: { type: TypeDetenteurEnum.LaCommunaute, ids: detenteurIds },
          });
        } else {
          result.push({
            label: `${detenteur.label} & ${coDetenteur.label}`,
            value: { type: TypeDetenteurEnum.LaCommunaute, ids: detenteurIds },
          });
        }
      }
    }
    if (categorie === 'actif') {
      result.push({ label: 'Multi-Propriétaires et/ou démembrement', value: { type: TypeDetenteurEnum.Multiproprietaires, ids: 'Multi' } });
    }
    return result;
  }
  public getDetenteurOption(investisseurId: number, personne: PersonnePhysique): DetenteurOption {
    const { nom, prenom } = personne;
    const detenteur = {
      label: `${nom ?? ''} ${prenom ?? ''}`.trim(),
      value: { type: TypeDetenteurEnum.Investisseur, ids: investisseurId.toString() },
      data: investisseurId.toString(),
    };
    return detenteur;
  }
  public async getFamily(investisseur?: Investisseur, coupleOnly: boolean = false): Promise<DetenteurPersonne[]> {
    if (!investisseur) return [];
    const detenteurs: DetenteurPersonne[] = [];
    const ids = new Set<number>();
    const personne = investisseur.personnePhysique?.[0];
    if (!personne) return detenteurs;
    const addDetenteur = (d: DetenteurPersonne) => {
      if (!ids.has(d.id)) {
        ids.add(d.id);
        detenteurs.push(d);
      }
    };
    // Investisseur principal
    addDetenteur({
      id: investisseur.id,
      type: TypeDetenteurEnum.Investisseur,
      firstName: personne.prenom,
      lastName: personne.nom,
    });
    // Co-investisseur
    const coPersonne = personne.relationCouplePersonnePhysique?.autrePersonnePhysique;
    const coInvestisseur = coPersonne?.investisseur?.[0];
    if (coPersonne && coInvestisseur) {
      addDetenteur({
        id: coInvestisseur.id,
        type: TypeDetenteurEnum.Investisseur,
        firstName: coPersonne.prenom,
        lastName: coPersonne.nom,
      });
    }
    if (coupleOnly) {
      return detenteurs;
    }
    // Relations famille
    const data = await this.investisseurService.getInvestisseursRelations();
    const relations = [
      ...(data?.investisseur?.personnePhysique?.[0]?.relationFamillePersonnePhysique ?? []),
      ...(data?.coInvestisseur?.personnePhysique?.[0]?.relationFamillePersonnePhysique ?? []),
    ];
    relations.forEach(rel => {
      const p = rel.autrePersonnePhysique;
      const inv = p?.investisseur?.[0];
      if (!p || !inv) return;
      addDetenteur({
        id: inv.id,
        type: TypeDetenteurEnum.RelationFamille,
        firstName: p.prenom,
        lastName: p.nom,
      });
    });
    return detenteurs;
  }
  // END REFACTO
  // Options to be removed
  readonly modeDetentionOption: OuiSelectOption<TypeDetentionEnum>[] = [
    {
      label: 'Pleine propriété',
      value: TypeDetentionEnum.PleinePropriete,
    },
    {
      label: 'Usufruit',
      value: TypeDetentionEnum.Usufruit,
    },
    {
      label: 'Nue-propriété',
      value: TypeDetentionEnum.NuePropriete,
    },
  ];
  getDuplicate(basePatrimoine: Patrimoine): Patrimoine {
    const designation = basePatrimoine.designation ? `${basePatrimoine.designation} (Copie)` : undefined;
    const patrimoineActif = basePatrimoine.patrimoineActif
      ? ({ ...basePatrimoine.patrimoineActif, patrimoinesPassifs: [] } as Actif)
      : undefined;
    const patrimoinePassif = basePatrimoine.patrimoinePassif
      ? ({ ...basePatrimoine.patrimoinePassif, patrimoineActifId: undefined, patrimoineActif: undefined } as Passif)
      : undefined;
    const result: Patrimoine = {
      ...basePatrimoine,
      id: undefined,
      designation: designation,
      patrimoineBudgets: [],
      patrimoineActif: patrimoineActif,
      patrimoinePassif: patrimoinePassif,
    };
    return result;
  }
  async save(patrimoineInput: PatrimoineInput): Promise<number> {
    const investisseurPatrimoine = await this.queryManagerService.handleMutationErrorsAsync(
      this.createOrUpdatePatrimoineGQL.mutate({
        variables: { patrimoineInput: patrimoineInput },
      })
    );
    const patrimoine = investisseurPatrimoine.data?.createOrUpdatePatrimoine.patrimoine;
    if (patrimoine) {
      this.requestPatrimoinesReload();
      await this.investisseurService.refreshRecueilState();
      return patrimoine.id;
    } else {
      this.snackbarService.open("Une erreur est survenue veuillez réessayer ou contacter l'équipe de support.", 'error');
      throw investisseurPatrimoine.error;
    }
  }
  async updateContrat(patrimoineInput: PatrimoineInput): Promise<number> {
    const investisseurPatrimoine = await this.queryManagerService.handleMutationErrorsAsync(
      this.updatePatrimoineContratGQL.mutate({
        variables: { patrimoineInput: patrimoineInput },
      })
    );
    const patrimoine = investisseurPatrimoine.data?.updatePatrimoineContrat?.patrimoine;
    if (patrimoine) {
      this.requestPatrimoinesReload();
      await this.investisseurService.refreshRecueilState();
      return patrimoine.id;
    } else {
      this.snackbarService.open("Une erreur est survenue veuillez réessayer ou contacter l'équipe de support.", 'error');
      throw investisseurPatrimoine.error;
    }
  }
  async processRetraite(investisseurId: number): Promise<void> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.manageInvestisseurRetraiteGQL.mutate({
        variables: { investisseurId: investisseurId },
      })
    );
    if (!result) {
      this.snackbarService.open(
        "Une erreur est survenue lors de la mise à jour veuillez réessayer ou contacter l'équipe de support.",
        'error'
      );
    } else {
      this.requestPatrimoinesReload();
      await this.investisseurService.refreshRecueilState();
      this.snackbarService.open('Patrimoine(s) mis à jour avec succès.', 'success');
    }
  }
  async deletePatrimoine(patrimoineId: number): Promise<void> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.deletePatrimoineGQL.mutate({
        variables: { id: patrimoineId },
      })
    );
    if (!result) {
      this.snackbarService.open(
        "Une erreur est survenue lors de la suprression veuillez réésailler ou contacter l'équipe de support.",
        'error'
      );
    } else {
      this.requestPatrimoinesReload();
      this.budgetService.requestBudgetsReload();
      await this.investisseurService.refreshRecueilState();
      this.snackbarService.open('Patrimoine supprimé avec succès.', 'success');
    }
  }
  getPatrimoineFiscalValue(pat: PatrimoineView, investisseurs: number[]): number {
    const invPat = (pat.investisseurPatrimoines as InvestisseurPatrimoineView[]).filter(ip => investisseurs.includes(ip.investisseurId));
    if (invPat.length === 0) return 0;
    return invPat.reduce((acc, inv) => acc + (inv.valeurDetention ?? 0), 0);
  }
  private getValeurBien(patrimoine: Patrimoine): number {
    return patrimoine.patrimoineActif
      ? (patrimoine.patrimoineActif.valorisationDuDroitDemembre ?? patrimoine.patrimoineActif.valeurEstimee)
      : patrimoine.patrimoinePassif
        ? patrimoine.patrimoinePassif.capitalRestantDu
        : (patrimoine.patrimoinePrevoyance?.capitalAssure ?? 0);
  }
  private getInvestisseurPatrimoinesDisplayed(
    patrimoine: Patrimoine,
    fiscalMap: Map<number, number> | null,
    fiscalMapSecurisee: Map<number, number> | null
  ): InvestisseurPatrimoineView[] {
    let investisseurPatrimoines = patrimoine.investisseurPatrimoines.map(ip =>
      this.getInvestisseurPatrimoineView(patrimoine, ip, fiscalMap, fiscalMapSecurisee)
    );
    const isCommunaute =
      patrimoine.patrimoineActif !== null &&
      patrimoine.investisseurPatrimoines?.some(ip => ip.typeDetenteur === TypeDetenteurEnum.LaCommunaute);
    // Case communauté : display only Communauté instead of both investisseurs
    if (isCommunaute) {
      const communauteInvestisseurs = investisseurPatrimoines.filter(ip => ip.investisseurId > 0);
      const rawDateFin = patrimoine.patrimoinePassif?.dateFin;

      const mergedInvestisseur: InvestisseurPatrimoineView = {
        pourcentageDetentionDisplay: this.getPourcentageCommunauteDisplay(communauteInvestisseurs[0]),
        valeurDetention: communauteInvestisseurs.reduce((acc, inv) => acc + (inv.valeurDetention ?? 0), 0),
        epargneSecurisee: communauteInvestisseurs.reduce((acc, inv) => acc + (inv.epargneSecurisee ?? 0), 0),
        investisseurName: 'Communauté',
        investisseurId: 0,
        isInvestisseurFiche: true,
        dateFinPassif: rawDateFin ? new Date(rawDateFin) : null,
        mensualiteDetention: communauteInvestisseurs.reduce((acc, inv) => acc + (inv.mensualiteDetention ?? 0), 0),
      } as InvestisseurPatrimoineView;

      investisseurPatrimoines = investisseurPatrimoines.filter(ip => ip.investisseurId <= 0);
      investisseurPatrimoines.unshift(mergedInvestisseur);
    }

    return investisseurPatrimoines;
  }
  private getInvestisseurPatrimoineView(
    patrimoine: Patrimoine,
    inv: InvestisseurPatrimoine,
    fiscalMap: Map<number, number> | null,
    fiscalMapSecurisee: Map<number, number> | null
  ): InvestisseurPatrimoineView {
    let mensualiteDetention = 0;
    if (patrimoine.patrimoinePassif) {
      const baseMensualite = this.getCurrentMensualite(patrimoine.patrimoinePassif);
      const tauxDetention = inv.detentionPP ?? 0;
      mensualiteDetention = (baseMensualite * tauxDetention) / 100;
    }

    const rawDateFin = patrimoine.patrimoinePassif?.dateFin;

    return {
      ...inv,
      pourcentageDetentionDisplay: this.getPourcentageDetentionDisplay(inv),
      valeurDetention: fiscalMap?.get(inv.id) ?? 0,
      epargneSecurisee: fiscalMapSecurisee?.get(inv.id) ?? 0,
      isInvestisseurFiche: this.investisseurIds()?.includes(inv.investisseurId!),
      investisseurName:
        inv.investisseurId > 0
          ? inv.investisseur?.personnePhysique?.[0]
            ? `${inv.investisseur.personnePhysique[0].prenom} ${inv.investisseur.personnePhysique[0].nom}`
            : null
          : 'Usufruitier',
      dateFinPassif: rawDateFin ? new Date(rawDateFin) : null,
      mensualiteDetention: mensualiteDetention,
    };
  }
  private getPourcentageDetentionDisplay(inv: InvestisseurPatrimoine): string {
    const result = [];
    if (inv.detentionPP && inv.detentionPP > 0) {
      result.push(`${inv.detentionPP} % PP`);
    }
    if (inv.detentionNP && inv.detentionNP > 0) {
      result.push(`${inv.detentionNP} % NP`);
    }
    if (inv.detentionUS && inv.detentionUS > 0) {
      result.push(`${inv.detentionUS} % US`);
    }
    return result.join(' - ');
  }
  private getPourcentageCommunauteDisplay(inv: InvestisseurPatrimoine): string {
    if (inv.detentionPP && inv.detentionPP > 0) {
      return '100 % PP';
    }
    if (inv.detentionNP && inv.detentionNP > 0) {
      return '100 % NP';
    }
    if (inv.detentionUS && inv.detentionUS > 0) {
      return '100 % US';
    }
    return '';
  }
  private getType(row: Patrimoine) {
    switch (row.patrimoineCategorie.patrimoineCategory) {
      case PatrimoineCategoryEnum.Actifs: {
        const type = actifTypes.find(s => s.value === row.patrimoineActif?.type)?.label;
        return type;
      }
      case PatrimoineCategoryEnum.Passif: {
        let type = passifTypes.find(s => s.value === row.patrimoinePassif?.type)?.label;
        if (!type) type = row.patrimoineCategorie.subCategory2Libelle ?? '';
        return type;
      }
      case PatrimoineCategoryEnum.Prevoyance: {
        let type = prevoyanceTypes.find(s => s.value === row.patrimoinePrevoyance?.type)?.label;
        if (!type) type = row.patrimoineCategorie.subCategoryLibelle ?? '';
        return type;
      }
      default:
        return '';
    }
  }

  /**
   * Calcule la mensualité actuelle d'un passif en fonction de la date du jour
   * et de la gestion spécifique des crédits par paliers.
   */
  private getCurrentMensualite(passif: Passif | undefined | null): number {
    if (!passif) return 0;
    if (passif.type !== TypePassif.ParPalier) {
      return passif.mensualite ?? 0;
    }

    if (!passif.dateDebut) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalisation à minuit pour éviter les effets de bord d'heures

    let start = new Date(passif.dateDebut);
    start.setHours(0, 0, 0, 0);

    if (start > today) return 0;

    // PALIER 1
    if (passif.dureePremierPalier != null && passif.mensualitePremierPalier != null) {
      const end = new Date(start.getTime());
      end.setMonth(end.getMonth() + passif.dureePremierPalier);

      if (today < end) return passif.mensualitePremierPalier;
      start = end;
    }

    // PALIER 2
    if (passif.dureeSecondPalier != null && passif.mensualiteSecondPalier != null) {
      const end = new Date(start.getTime());
      end.setMonth(end.getMonth() + passif.dureeSecondPalier);

      if (today < end) return passif.mensualiteSecondPalier;
      start = end;
    }
    // PALIER 3
    if (passif.dureeTroisiemePalier != null && passif.mensualiteTroisiemePalier != null) {
      const end = new Date(start.getTime());
      end.setMonth(end.getMonth() + passif.dureeTroisiemePalier);

      if (today < end) return passif.mensualiteTroisiemePalier;
    }

    return 0;
  }
}
